import { AdminSectionCard } from '../primitives/admin-section-card.js';
import { AdminEmptyState } from '../primitives/admin-empty-state.js';
import { AdminErrorState } from '../primitives/admin-error-state.js';
import { AdminSkeleton } from '../primitives/admin-skeleton.js';
import { formatCount, formatPaise } from '../../lib/format.js';
import type { TopSellingProductDto } from '../../types/dashboard.js';

export interface TopSellingProductsProps {
  products: TopSellingProductDto[];
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
}

/** Bounded (server caps at 5), ranked by quantity sold for the selected period -- read-only, no navigation to a product-detail route this milestone (Part 9), same as `RecentOrdersList`. */
export function TopSellingProducts({ products, isLoading, isError, onRetry }: TopSellingProductsProps) {
  return (
    <AdminSectionCard title="Top selling products" description="Best sellers by quantity for the selected period">
      {isLoading && <AdminSkeleton lines={4} className="h-10" />}
      {!isLoading && isError && (
        <AdminErrorState message="Top selling products could not be loaded." onRetry={onRetry} />
      )}
      {!isLoading && !isError && products.length === 0 && (
        <AdminEmptyState title="No sales yet" description="Top sellers will appear here once orders start coming in." />
      )}
      {!isLoading && !isError && products.length > 0 && (
        <ul className="divide-y divide-gray-50">
          {products.map((product, index) => (
            <li
              key={product.variantId ?? `${product.sku}-${index}`}
              className="flex items-center justify-between gap-3 py-2.5"
            >
              <div className="flex min-w-0 items-center gap-3">
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-gray-50 text-xs font-medium text-gray-500">
                  {index + 1}
                </span>
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-gray-900">
                    {product.productName}{' '}
                    <span className="font-normal text-gray-500">— {product.variantName}</span>
                  </p>
                  <p className="text-xs text-gray-400">SKU {product.sku}</p>
                </div>
              </div>
              <div className="shrink-0 text-right">
                <p className="text-sm font-medium text-gray-900">{formatPaise(product.revenuePaise)}</p>
                <p className="text-xs text-gray-500">{formatCount(product.quantitySold)} sold</p>
              </div>
            </li>
          ))}
        </ul>
      )}
    </AdminSectionCard>
  );
}
